import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface ErrorBoundaryProps {
  children: ReactNode;
  level?: 'app' | 'page';
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, errorInfo);
    }
    // Report to Sentry in production
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const level = this.props.level ?? 'app';

    if (level === 'app') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))]">
          <div className="text-center p-8 max-w-md">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <span className="text-2xl">!</span>
            </div>
            <h1 className="text-xl font-bold mb-2 text-[hsl(var(--foreground))]">
              Nešto je pošlo po krivu
            </h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
              Došlo je do neočekivane greške. Molimo osvježite stranicu.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <pre className="text-xs text-left bg-[hsl(var(--muted))] p-3 rounded mb-4 overflow-auto max-h-32">
                {this.state.error.message}
              </pre>
            )}
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Osvježi stranicu
            </button>
          </div>
        </div>
      );
    }

    // Page-level error boundary - inline, non-destructive
    return (
      <div className="flex items-center justify-center py-16 px-4">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <span className="text-lg">!</span>
          </div>
          <h2 className="text-lg font-semibold mb-1 text-[hsl(var(--foreground))]">
            Greška na stranici
          </h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
            Ova sekcija je naišla na problem.
          </p>
          {import.meta.env.DEV && this.state.error && (
            <pre className="text-xs text-left bg-[hsl(var(--muted))] p-3 rounded mb-4 overflow-auto max-h-24">
              {this.state.error.message}
            </pre>
          )}
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-5 py-2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Pokušaj ponovo
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
